import { Construction, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Logo } from '@/components/brand/logo';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface PlaceholderPageProps {
  title: string;
  description: string;
}

/**
 * Estado vacío para módulos aún no portados al panel nuevo.
 *
 * Se usa en lugar de un 404 para dejar claro que el módulo existe, que el
 * usuario tiene permiso y que la pantalla llegará en una siguiente entrega.
 */
export function PlaceholderPage({ title, description }: PlaceholderPageProps) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-lg rounded-card border border-dashed border-ink-200 bg-white/70 p-10 text-center">
        <span className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
          <Construction className="size-7" aria-hidden="true" />
        </span>

        <h1 className="mt-5 text-xl font-bold tracking-tight text-ink-900">{title}</h1>

        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-ink-500">{description}</p>

        <div className="mt-5 flex justify-center">
          <Badge variant="warning">Módulo en construcción</Badge>
        </div>

        <p className="mt-6 text-xs leading-relaxed text-ink-400">
          Mientras tanto, este módulo sigue operativo en el panel anterior de EDSuite.
        </p>

        <div className="mt-6 flex justify-center">
          <Link to="/dashboard">
            <Button variant="outline" leftIcon={<Home className="size-4" />}>
              Volver al dashboard
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

interface ErrorPageProps {
  code: string;
  title: string;
  description: string;
}

/** Página de error reutilizable (403, 404, fallos de la app). */
export function ErrorPage({ code, title, description }: ErrorPageProps) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-50 px-5 py-10">
      <div className="w-full max-w-md text-center">
        <Logo size="sm" className="justify-center" />

        <p className="mt-8 font-mono text-5xl font-bold text-brand-500">{code}</p>
        <h1 className="mt-3 text-xl font-bold tracking-tight text-ink-900">{title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-ink-500">{description}</p>

        <div className="mt-7 flex justify-center gap-3">
          <Link to="/dashboard">
            <Button leftIcon={<Home className="size-4" />}>Ir al dashboard</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
